import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  noticeCard: {
    border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px",
    marginBottom: "12px", transition: "box-shadow 0.2s"
  },
  noticeTitle: { fontWeight: "bold", color: "#1a237e", fontSize: "15px", margin: "0 0 6px" },
  noticeBody: { color: "#555", fontSize: "13px", lineHeight: "1.7", margin: "0 0 10px" },
  noticeMeta: { fontSize: "11px", color: "#999", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" },
  pinnedBadge: { fontSize: "11px", fontWeight: "bold", color: "#e65100", marginRight: "4px" }
};

const TABS = [
  { label: "All", value: "" },
  { label: "Announcements", value: "announcement" },
  { label: "Events", value: "event" },
  { label: "Circulars", value: "circular" }
];

const TYPE_COLORS = {
  announcement: { bg: "#e3f2fd", text: "#0d47a1", border: "#1565c0" },
  event:        { bg: "#f3e5f5", text: "#6a1b9a", border: "#7b1fa2" },
  circular:     { bg: "#fff8e1", text: "#e65100", border: "#ef6c00" },
  general:      { bg: "#e8eaf6", text: "#1a237e", border: "#1a237e" },
  academic:     { bg: "#e8f5e9", text: "#2e7d32", border: "#2e7d32" },
  fees:         { bg: "#fff3e0", text: "#e65100", border: "#e65100" },
  exams:        { bg: "#fce4ec", text: "#880e4f", border: "#880e4f" },
  hr:           { bg: "#f5f5f5", text: "#333",    border: "#999" },
  events:       { bg: "#f3e5f5", text: "#6a1b9a", border: "#7b1fa2" }
};

const AUDIENCE_OPTIONS = ["All", "Students", "Staff", "Parents", "Lecturers"];
const BLANK = { title: "", content: "", type: "announcement", targetAudience: "All" };

class Announcements extends Component {
  state = {
    notices: [],
    loading: true,
    tab: 0,
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() { this.fetchNotices(); }

  fetchNotices = async () => {
    this.setState({ loading: true });
    try {
      const { tab } = this.state;
      const params = { limit: 100, sort: "pinned,createdAt", order: "desc" };
      const typeVal = TABS[tab] ? TABS[tab].value : "";
      if (typeVal) params.type = typeVal;
      const res = await api.get("/notices", { params });
      const d = res.data.data || res.data;
      this.setState({ notices: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ notices: [], loading: false });
    }
  };

  handleTabChange = (_, v) => this.setState({ tab: v }, this.fetchNotices);

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null });

  openEdit = n => {
    this.setState({
      open: true, editing: n.id, error: null,
      form: {
        title: n.title || "",
        content: n.message || n.content || "",
        type: n.type || n.category || "announcement",
        targetAudience: n.targetAudience || n.audience || "All"
      }
    });
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.title || !form.content) {
      this.setState({ error: "Title and content are required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      const payload = {
        title: form.title,
        message: form.content,
        content: form.content,
        category: form.type,
        type: form.type,
        audience: form.targetAudience ? form.targetAudience.toLowerCase() : "all",
        targetAudience: form.targetAudience
      };
      if (editing) {
        await api.put(`/notices/${editing}`, payload);
      } else {
        await api.post("/notices", payload);
      }
      this.setState({ open: false, saving: false });
      this.fetchNotices();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleDelete = async id => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await api.delete(`/notices/${id}`);
      this.fetchNotices();
    } catch (e) { /* silent */ }
  };

  handlePin = async (id, pinned) => {
    try {
      await api.patch(`/notices/${id}`, { pinned: !pinned });
      this.fetchNotices();
    } catch (e) { /* silent */ }
  };

  filterNoticesByTab = notices => {
    const { tab } = this.state;
    const typeVal = TABS[tab] ? TABS[tab].value : "";
    if (!typeVal) return notices;
    return notices.filter(n => (n.type || n.category || "").toLowerCase() === typeVal);
  };

  render() {
    const { classes } = this.props;
    const { notices, loading, tab, open, editing, form, saving, error } = this.state;

    const filtered = this.filterNoticesByTab(notices);
    const pinned = filtered.filter(n => n.pinned);
    const unpinned = filtered.filter(n => !n.pinned);
    const ordered = [...pinned, ...unpinned];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Announcements & Notices</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    {filtered.length} notice{filtered.length !== 1 ? "s" : ""} &middot; {pinned.length} pinned
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={this.openAdd}>
                    <Icon style={{ marginRight: "4px" }}>add_alert</Icon> Create Notice
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              <Tabs
                value={tab}
                onChange={this.handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                style={{ marginBottom: "16px", borderBottom: "1px solid #e0e0e0" }}
              >
                {TABS.map((t, i) => <Tab key={i} label={t.label} />)}
              </Tabs>

              {loading ? (
                <div style={{ textAlign: "center", padding: "30px" }}><CircularProgress /></div>
              ) : ordered.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                  <Icon style={{ fontSize: "48px", color: "#e0e0e0", display: "block", margin: "0 auto 12px" }}>notifications_none</Icon>
                  <p>No notices in this category.</p>
                  <Button size="sm" color="primary" onClick={this.openAdd}>
                    Create First Notice
                  </Button>
                </div>
              ) : (
                <GridContainer>
                  {ordered.map(n => {
                    const typeKey = (n.type || n.category || "general").toLowerCase();
                    const cc = TYPE_COLORS[typeKey] || TYPE_COLORS.general;
                    return (
                      <GridItem xs={12} md={6} key={n.id}>
                        <div
                          className={classes.noticeCard}
                          style={{
                            borderLeft: `4px solid ${cc.border}`,
                            background: n.pinned ? "#fffde7" : "#fff"
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", marginBottom: "8px" }}>
                                {n.pinned && (
                                  <span className={classes.pinnedBadge}>&#128204; PINNED</span>
                                )}
                                <Chip
                                  label={(n.type || n.category || "general").toUpperCase()}
                                  style={{ background: cc.bg, color: cc.text, fontSize: "10px", height: "18px" }}
                                />
                                {(n.targetAudience || n.audience) && (
                                  <Chip
                                    label={n.targetAudience || n.audience}
                                    style={{ background: "#f5f5f5", color: "#555", fontSize: "10px", height: "18px" }}
                                  />
                                )}
                              </div>
                              <p className={classes.noticeTitle}>{n.title}</p>
                              <p className={classes.noticeBody}>
                                {(n.message || n.content || "").length > 180
                                  ? (n.message || n.content).substring(0, 180) + "..."
                                  : (n.message || n.content || "")}
                              </p>
                              <div className={classes.noticeMeta}>
                                <span>
                                  <Icon style={{ fontSize: "12px", verticalAlign: "middle", marginRight: "2px" }}>person</Icon>
                                  {n.author?.name || n.createdBy || "Admin"}
                                </span>
                                <span>
                                  <Icon style={{ fontSize: "12px", verticalAlign: "middle", marginRight: "2px" }}>event</Icon>
                                  {n.createdAt ? new Date(n.createdAt).toLocaleDateString("en-UG") : ""}
                                </span>
                              </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px", marginLeft: "8px" }}>
                              <Button size="sm" simple onClick={() => this.handlePin(n.id, n.pinned)} title={n.pinned ? "Unpin" : "Pin"}>
                                <Icon style={{ fontSize: "16px" }}>push_pin</Icon>
                              </Button>
                              <Button size="sm" color="info" simple onClick={() => this.openEdit(n)}>
                                <Icon style={{ fontSize: "16px" }}>edit</Icon>
                              </Button>
                              <Button size="sm" color="danger" simple onClick={() => this.handleDelete(n.id)}>
                                <Icon style={{ fontSize: "16px" }}>delete</Icon>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </GridItem>
                    );
                  })}
                </GridContainer>
              )}
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Notice" : "Create Notice"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Title *" id="annTitle" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.title, onChange: this.handleChange("title") }} />
            <div style={{ marginTop: "16px" }}>
              <label style={{ fontSize: "12px", color: "#aaa", textTransform: "uppercase" }}>Content *</label>
              <textarea
                rows={6}
                value={form.content}
                onChange={this.handleChange("content")}
                style={{ width: "100%", marginTop: "4px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
                placeholder="Enter the full notice content..."
              />
            </div>
            <GridContainer>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Type</InputLabel>
                  <Select value={form.type} onChange={this.handleChange("type")}>
                    <MenuItem value="announcement">Announcement</MenuItem>
                    <MenuItem value="event">Event</MenuItem>
                    <MenuItem value="circular">Circular</MenuItem>
                    <MenuItem value="general">General</MenuItem>
                    <MenuItem value="academic">Academic</MenuItem>
                    <MenuItem value="fees">Fees</MenuItem>
                    <MenuItem value="exams">Exams</MenuItem>
                    <MenuItem value="hr">HR</MenuItem>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Target Audience</InputLabel>
                  <Select value={form.targetAudience} onChange={this.handleChange("targetAudience")}>
                    {AUDIENCE_OPTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : editing ? "Update" : "Post"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

Announcements.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(Announcements));
